import { useState, useCallback } from "react";
import { db } from "../config/firebase";
import { doc, collection, getDocs, getDoc } from "firebase/firestore";
import { stepConfigs } from "../_start/partials/widgets/forms/stepConfig";

interface StepData {
  stepNumber: number;
  title: string;
  category: string;
  fields: { label: string; value: string }[];
  progress: number;
}

interface ProjectExport {
  projectTitle: string;
  projectDescription: string;
  overallProgress: number;
  score?: number;
  strengths?: string[];
  redFlags?: string[];
  recommendations?: string[];
  categories: {
    name: string;
    score?: number;
    steps: StepData[];
  }[];
  exportedAt: string;
}

const CATEGORIES = [
  { id: "marketOpportunity", label: "Market Opportunity", steps: [1, 2, 3, 4, 5] },
  { id: "customerUnderstanding", label: "Customer Understanding", steps: [6, 7, 8, 9, 10] },
  { id: "valueAndProduct", label: "Value & Product", steps: [11, 12, 13, 14] },
  { id: "businessModel", label: "Business Model", steps: [15, 16, 17, 18, 19] },
  { id: "validationExecution", label: "Validation & Execution", steps: [20, 24] },
];

const stepTitles: Record<number, string> = {
  1: "Market Segmentation",
  2: "Select a Beachhead Market",
  3: "Build an End User Profile",
  4: "Calculate TAM for Beachhead",
  5: "Profile the Persona",
  6: "Full Life Cycle Use Case",
  7: "High-Level Product Specification",
  8: "Quantify the Value Proposition",
  9: "Identify Next 10 Customers",
  10: "Define Your Core",
  11: "Chart Competitive Position",
  12: "Determine Customer's DMU",
  13: "Map Process to Acquire Customer",
  14: "Calculate TAM for Follow-on",
  15: "Design a Business Model",
  16: "Set Your Pricing Framework",
  17: "Calculate LTV",
  18: "Map the Sales Process",
  19: "Calculate COCA",
  20: "Identify Key Assumptions",
  21: "Test Key Assumptions",
  22: "Define the MVP",
  23: "Show Dogs Will Eat the Food",
  24: "Develop a Product Plan",
};

export const usePDFExport = () => {
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const collectProjectData = useCallback(
    async (userID: string, projectID: string): Promise<ProjectExport | null> => {
      try {
        const userRef = doc(db, "users", userID);
        const projectRef = doc(userRef, "projects", projectID);

        // Get project data
        const projectDoc = await getDoc(projectRef);
        if (!projectDoc.exists()) return null;
        const projectData = projectDoc.data();

        // Get all step progress
        const progressRef = collection(projectRef, "progress");
        const progressSnapshot = await getDocs(progressRef);

        const stepsMap: Record<number, { form: Record<string, string>; stepProgress: number }> = {};
        progressSnapshot.docs.forEach((d) => {
          const stepNum = parseInt(d.id.replace("step", ""), 10);
          stepsMap[stepNum] = {
            form: d.data().form || {},
            stepProgress: d.data().stepProgress || 0,
          };
        });

        // Get score
        const scoreRef = doc(projectRef, "scores", "latest");
        const scoreDoc = await getDoc(scoreRef);
        const scoreData = scoreDoc.exists() ? scoreDoc.data() : null;

        // Build export
        const categories = CATEGORIES.map((cat) => ({
          name: cat.label,
          score: scoreData?.categories?.[cat.id],
          steps: cat.steps.map((stepNum): StepData => {
            const stepData = stepsMap[stepNum] || { form: {}, stepProgress: 0 };
            const config = stepConfigs[stepNum];
            const fields: { label: string; value: string }[] = [];

            const allFields = config?.sections?.flatMap((s) => s.fields) || [];
            if (allFields.length > 0) {
              allFields.forEach((field) => {
                const value = stepData.form[field.name];
                if (value && value.trim()) {
                  fields.push({ label: field.label || field.name, value });
                }
              });
            }

            return {
              stepNumber: stepNum,
              title: stepTitles[stepNum] || `Step ${stepNum}`,
              category: cat.label,
              fields,
              progress: stepData.stepProgress,
            };
          }),
        }));

        return {
          projectTitle: projectData.projectTitle || "Untitled Project",
          projectDescription: projectData.projectDescription || "",
          overallProgress: projectData.projectProgress || 0,
          score: scoreData?.overall,
          strengths: scoreData?.strengths,
          redFlags: scoreData?.redFlags,
          recommendations: scoreData?.recommendations,
          categories,
          exportedAt: new Date().toISOString(),
        };
      } catch (err) {
        console.error("Error collecting project data:", err);
        return null;
      }
    },
    []
  );

  const generateHTML = (data: ProjectExport): string => {
    const scoreColor = (s: number) => {
      if (s >= 80) return "#10b981";
      if (s >= 60) return "#667eea";
      if (s >= 40) return "#f59e0b";
      return "#ef4444";
    };

    const categoriesHTML = data.categories
      .map(
        (cat) => `
      <div style="page-break-inside: avoid; margin-bottom: 32px;">
        <div style="display: flex; align-items: center; margin-bottom: 16px; padding: 12px 16px; background: #f3f4f6; border-radius: 8px;">
          <h2 style="margin: 0; font-size: 18px; color: #1f2937; flex-grow: 1;">${cat.name}</h2>
          ${cat.score !== undefined ? `<span style="font-size: 16px; font-weight: bold; color: ${scoreColor(cat.score)}">${cat.score}%</span>` : ""}
        </div>
        ${cat.steps
          .map(
            (step) => `
          <div style="margin-bottom: 20px; padding-left: 16px; border-left: 3px solid #e5e7eb;">
            <h3 style="font-size: 15px; color: #374151; margin-bottom: 8px;">
              Step ${step.stepNumber}: ${step.title}
              <span style="font-size: 12px; color: #9ca3af; margin-left: 8px;">${step.progress}%</span>
            </h3>
            ${
              step.fields.length > 0
                ? step.fields
                    .map(
                      (f) => `
                <div style="margin-bottom: 10px;">
                  <label style="display: block; font-size: 12px; color: #6b7280; margin-bottom: 2px; font-weight: 600;">${f.label}</label>
                  <p style="margin: 0; font-size: 13px; color: #374151; line-height: 1.5;">${f.value}</p>
                </div>
              `
                    )
                    .join("")
                : '<p style="color: #9ca3af; font-size: 13px; font-style: italic;">Not yet completed</p>'
            }
          </div>
        `
          )
          .join("")}
      </div>
    `
      )
      .join("");

    return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>${data.projectTitle} - Business Validation Report</title>
      <style>
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .page-break { page-break-before: always; }
        }
        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          color: #1f2937;
          margin: 0;
          padding: 40px;
          font-size: 14px;
          line-height: 1.6;
        }
      </style>
    </head>
    <body>
      <!-- Cover -->
      <div style="text-align: center; padding: 60px 0 40px;">
        <div style="display: inline-block; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 12px 24px; border-radius: 24px; font-size: 14px; font-weight: bold; margin-bottom: 24px;">
          Business Validation Report
        </div>
        <h1 style="font-size: 32px; margin: 0 0 8px; color: #1f2937;">${data.projectTitle}</h1>
        <p style="font-size: 16px; color: #6b7280; max-width: 600px; margin: 0 auto 24px;">
          ${data.projectDescription || "Business idea validation report"}
        </p>
        ${
          data.score !== undefined
            ? `
          <div style="display: inline-block; padding: 20px 40px; border-radius: 16px; background: #f9fafb; border: 2px solid ${scoreColor(data.score)};">
            <div style="font-size: 48px; font-weight: bold; color: ${scoreColor(data.score)};">${data.score}</div>
            <div style="font-size: 14px; color: #6b7280;">Business Score / 100</div>
          </div>
        `
            : ""
        }
        <p style="font-size: 12px; color: #9ca3af; margin-top: 24px;">
          Generated by Business Foundry &bull; ${new Date(data.exportedAt).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
        </p>
      </div>

      <!-- Executive Summary -->
      ${
        data.score !== undefined
          ? `
        <div style="margin-bottom: 40px; padding: 24px; background: #f9fafb; border-radius: 12px;">
          <h2 style="font-size: 20px; margin: 0 0 16px;">Executive Summary</h2>
          <div style="display: flex; gap: 24px; flex-wrap: wrap;">
            <div style="flex: 1; min-width: 200px;">
              <h3 style="font-size: 14px; color: #10b981; margin: 0 0 8px;">Strengths</h3>
              ${(data.strengths || []).map((s) => `<p style="margin: 0 0 4px; font-size: 13px; color: #374151;">&#x2713; ${s}</p>`).join("")}
            </div>
            <div style="flex: 1; min-width: 200px;">
              <h3 style="font-size: 14px; color: #ef4444; margin: 0 0 8px;">Areas to Address</h3>
              ${(data.redFlags || []).map((f) => `<p style="margin: 0 0 4px; font-size: 13px; color: #374151;">&#x26A0; ${f}</p>`).join("")}
            </div>
            <div style="flex: 1; min-width: 200px;">
              <h3 style="font-size: 14px; color: #667eea; margin: 0 0 8px;">Recommendations</h3>
              ${(data.recommendations || []).map((r, i) => `<p style="margin: 0 0 4px; font-size: 13px; color: #374151;">${i + 1}. ${r}</p>`).join("")}
            </div>
          </div>
        </div>
      `
          : ""
      }

      <!-- Categories -->
      ${categoriesHTML}

      <!-- Footer -->
      <div style="text-align: center; padding: 32px 0; border-top: 1px solid #e5e7eb; margin-top: 32px;">
        <p style="font-size: 12px; color: #9ca3af; margin: 0;">
          Business Foundry &bull; businessfactory.ai &bull; ${data.overallProgress}% Complete
        </p>
      </div>
    </body>
    </html>
    `;
  };

  const exportPDF = useCallback(
    async (userID: string, projectID: string) => {
      setIsExporting(true);
      setError(null);

      try {
        const data = await collectProjectData(userID, projectID);
        if (!data) {
          setError("Could not load project data");
          return;
        }

        const html = generateHTML(data);

        // Open in new window for printing
        const printWindow = window.open("", "_blank");
        if (printWindow) {
          printWindow.document.write(html);
          printWindow.document.close();

          // Auto-trigger print dialog after a short delay
          setTimeout(() => {
            printWindow.print();
          }, 500);
        } else {
          setError("Please allow popups to export PDF");
        }
      } catch (err) {
        console.error("Export error:", err);
        setError("Failed to generate report");
      } finally {
        setIsExporting(false);
      }
    },
    [collectProjectData]
  );

  return {
    exportPDF,
    isExporting,
    error,
  };
};
